import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/ui/Icon';
import Footer from '../homepage/components/Footer';
import Modal from '../../components/ui/Modal';
import { userService } from '../../services/userService';

export default function SignIn() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, userProfile, loading: authLoading, signIn } = useAuth(); // Use userProfile from AuthContext
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [emailConfirmationRequired, setEmailConfirmationRequired] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(false);
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // Agreement Modal States
  const [showAgreement, setShowAgreement] = useState(false);
  const [agreementText, setAgreementText] = useState('');
  const [agreementVersionId, setAgreementVersionId] = useState(null);
  const [pendingDestination, setPendingDestination] = useState('/model-catalog');
  const [pendingUserId, setPendingUserId] = useState(null);

  // Authentication ready state depends on AuthContext's loading and userProfile status
  const isAuthReady = !authLoading && userProfile !== null;
  // Effective user is simply the user from AuthContext
  const effectiveUser = user;

  // Symmetrical Already-Authenticated Redirect:
  useEffect(() => {
    if (!authLoading && user) {
      navigate('/model-catalog');
    }
  }, [user, authLoading, navigate]);




  // Show loading state while checking auth
  if (authLoading) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Verifying authentication...</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const handleSignIn = async (e) => {
    e?.preventDefault();
    setError('');
    setEmailConfirmationRequired(false);
    setLoading(true);

    try {
      // Aggressively clean the email (removes all spaces, zero-width chars)
      const cleanEmail = email.replace(/[\s\u200B-\u200D\uFEFF]/g, '');

      // Sign in with AuthContext's signIn (DO NOT trim password, spaces are valid characters)
      const { data, error: signInError } = await signIn(cleanEmail, password);

      if (signInError) {
        if (signInError?.message?.includes('Email not confirmed')) {
          setEmailConfirmationRequired(true);
          setEmail(cleanEmail); // Store email for resend functionality
          throw new Error('Your email address needs to be confirmed. Please check your inbox for the confirmation email or click "Resend Confirmation Email" below.');
        } else if (signInError?.message?.includes('Invalid login credentials')) {
          throw new Error('Invalid email or password. Please try again.');
        } else {
          throw new Error(signInError?.message || 'Login failed');
        }
      }

      if (!data?.user || !data?.session) {
        throw new Error('Login failed. Please try again.');
      }

      // Ensure user profile exists
      const { data: userProfile } = await supabase
        ?.from('users')
        ?.select('id')
        ?.eq('id', data?.user?.id)
        ?.maybeSingle();

      if (!userProfile) {
        await supabase?.from('users')?.insert({
          id: data?.user?.id,
          email: data?.user?.email,
          first_name: data?.user?.user_metadata?.first_name || '',
          last_name: data?.user?.user_metadata?.last_name || '',
          created_at: new Date()?.toISOString()
        });
      }

      // Record explicit login session AFTER profile exists to prevent FK violations
      await userService.recordActivity(data.user.id, navigator.userAgent, "Login", window.location.pathname);

      // Check for Buyer Agreement
      const { data: activeAgreement } = await supabase
        .from('seller_agreements_versions')
        .select('*')
        .eq('agreement_type', 'buyer')
        .eq('is_active', true)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      const returnTo = sessionStorage.getItem('returnTo');
      const destination = returnTo || '/model-catalog';

      if (activeAgreement) {
         // Check if user has already accepted this exact version
         const { data: userAgreement } = await supabase
           .from('seller_agreements')
           .select('id')
           .eq('user_id', data.user.id)
           .eq('version_id', activeAgreement.id)
           .maybeSingle();

         if (!userAgreement) {
            // User hasn't accepted yet, show modal
            setAgreementText(activeAgreement.content);
            setAgreementVersionId(activeAgreement.id);
            setPendingUserId(data.user.id);
            setPendingDestination(destination);
            setShowAgreement(true);
            setLoading(false); // Stop loading since we're waiting for user input
            return;
         }
      }

      // ✅ CRITICAL FIX: Immediately navigate after successful authentication
      // Don't wait for AuthContext - navigate now
      console.log('SignIn - Redirecting immediately to model catalog');
      sessionStorage.removeItem('returnTo');
      
      // Reset loading state before navigation
      setLoading(false);
      
      // Navigate with replace to prevent back button issues
      navigate(destination, { replace: true });

    } catch (err) {
      setError(err?.message || 'Invalid login credentials');
      setLoading(false);
    }
  };

  const handleAcceptAgreement = async () => {
      try {
          if (pendingUserId && agreementVersionId) {
             const { error } = await supabase.from('seller_agreements').insert({
                 user_id: pendingUserId,
                 version_id: agreementVersionId,
                 user_agent: navigator.userAgent
             });
             if (error && error.code !== '23505') console.error('Error recording agreement:', error);
          }
      } catch (e) {
          console.error(e);
      } finally {
          setShowAgreement(false);
          sessionStorage.removeItem('returnTo');
          navigate(pendingDestination, { replace: true });
      }
  };

  const handleDeclineAgreement = async () => {
      alert("You will not be allowed to use OpenGadget services without accepting the Agreement.");
      setShowAgreement(false);
      try {
          await supabase.auth.signOut();
          window.location.href = '/signin'; // Force logout and return to signin
      } catch (e) {
          console.error(e);
          window.location.href = '/signin';
      }
  };

  const handleResendConfirmationEmail = async () => {
    if (!email?.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    setResendingEmail(true);
    setError('');

    try {
      const { error: resendError } = await supabase?.auth?.resend({
        type: 'signup',
        email: email
      });

      if (resendError) {
        throw new Error(resendError?.message || 'Failed to resend confirmation email');
      }

      setError('');
      alert('Confirmation email has been resent! Please check your inbox and spam folder.');
    } catch (err) {
      console.error('Resend confirmation error:', err);
      setError(err?.message || 'Failed to resend confirmation email');
    } finally {
      setResendingEmail(false);
    }
  };

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md mx-auto">
          <div className="bg-white rounded-lg shadow-md p-8">
            {/* Title */}
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900">Welcome Back</h2>
              <p className="text-gray-600 mt-2">Sign in to your account</p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                <div className="flex items-start">
                  <Icon name="AlertCircle" size={20} className="mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-sm">{error}</span>
                </div>
                {emailConfirmationRequired && (
                  <Button
                    type="button"
                    onClick={handleResendConfirmationEmail}
                    disabled={resendingEmail}
                    className="mt-3 w-full bg-red-600 hover:bg-red-700 text-white"
                    iconName={resendingEmail ? "Loader2" : "Mail"}
                    iconClassName={resendingEmail ? "animate-spin" : ""}
                    iconPosition="left"
                  >
                    {resendingEmail ? 'Sending...' : 'Resend Confirmation Email'}
                  </Button>
                )}
              </div>
            )}

            {/* Sign In Form */}
            <form onSubmit={handleSignIn} className="space-y-4">
              <Input
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e?.target?.value)}
                required
                placeholder="email@example.com"
              />
              <Input
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e?.target?.value)}
                required
              />

              <Button
                type="submit"
                disabled={loading}
                className="w-full"
                iconName={loading ? "Loader2" : "LogIn"}
                iconClassName={loading ? "animate-spin" : ""}
                iconPosition="right"
              >
                {loading ? 'Signing In...' : 'Sign In'}
              </Button>
            </form>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">Don't have an account?</span>
              </div>
            </div>

            {/* Sign Up Links */}
            <div className="space-y-3">
              <Button
                type="button"
                onClick={() => navigate('/signup')}
                className="w-full bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200"
                iconName="User"
                iconPosition="left"
              >
                Create free account
              </Button>
              <Button
                type="button"
                onClick={() => navigate('/sellers-toolbox')}
                className="w-full bg-blue-50 text-blue-600 hover:bg-blue-100 border border-blue-200"
                iconName="UserPlus"
                iconPosition="left"
              >
                Sign Up as Seller
              </Button>
              <Button
                type="button"
                onClick={() => navigate('/suppliers-resource')}
                className="w-full bg-purple-50 text-purple-600 hover:bg-purple-100 border border-purple-200"
                iconName="Building"
                iconPosition="left"
              >
                Sign Up as Supplier
              </Button>
            </div>

            {/* Forgot Password */}
            <div className="mt-6 text-center">
              <button
                type="button"
                onClick={() => navigate('/forgot-password')}
                className="text-sm text-gray-600 hover:text-gray-900"
              >
                Forgot your password?
              </button>
            </div>
          </div>

          {/* Help Text */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Need help?{' '}
              <button
                onClick={() => navigate('/support-center')}
                className="text-blue-600 hover:text-blue-700 font-medium"
              >
                Contact Support
              </button>
            </p>
          </div>
        </div>
      </div>
      <Footer />

      <Modal isOpen={showAgreement} onClose={handleDeclineAgreement} title="Updated Service Terms" size="max-w-4xl">
         <div className="p-8">
            <div className="mb-6 p-4 bg-blue-50 border border-blue-100 rounded-lg flex items-start gap-3">
               <Icon name="Info" className="text-blue-600 mt-0.5" size={20} />
               <p className="text-sm text-blue-800 font-medium">
                 We have updated our terms, please read and accept to continue use of the OpenGadget services.
               </p>
            </div>
            <div className="prose max-w-none mb-8 h-96 overflow-y-auto bg-white p-6 border rounded-xl shadow-inner text-slate-700" dangerouslySetInnerHTML={{ __html: agreementText }} />
            <div className="flex justify-end gap-4 pt-4 border-t">
                <button 
                  onClick={handleDeclineAgreement}
                  className="px-6 py-2.5 bg-white border border-gray-300 text-gray-700 font-bold rounded-lg hover:bg-gray-50 transition-all"
                >
                  Decline & Logout
                </button>
                <button 
                  onClick={handleAcceptAgreement}
                  className="px-8 py-2.5 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 shadow-lg shadow-blue-600/20 transition-all hover:scale-105 active:scale-95"
                >
                  I Accept
                </button>
            </div>
         </div>
      </Modal>
    </>
  );
}